import { createReducer, on } from '@ngrx/store';
import { ShoppingListActions } from './shopping-list.actions';
import { ShoppingListDto } from '@api/*';
import { ShoppingListStore, initialShoppingListStore } from './shopping-list.store';

// Backend returns the whole updated list here, not just the changed product --
// so we replace the whole list in state instead of patching one product into it.
function upsertShoppingList(state: ShoppingListStore, shoppingList: ShoppingListDto): ShoppingListStore {
    const exists = state.shoppingLists.some(list => list.id === shoppingList.id);
    return {
        ...state,
        shoppingLists: exists
            ? state.shoppingLists.map(list => (list.id === shoppingList.id ? shoppingList : list))
            : [...state.shoppingLists, shoppingList],
    };
}

export const shoppingListReducer = createReducer(
    initialShoppingListStore,

    // #region Loading
    on(
        ShoppingListActions.createShoppingList,
        ShoppingListActions.getAllShoppingLists,
        ShoppingListActions.getShoppingList,
        ShoppingListActions.updateShoppingListName,
        ShoppingListActions.deleteShoppingList,
        ShoppingListActions.addProductToShoppingList,
        ShoppingListActions.createShoppingListAndAddProduct,
        ShoppingListActions.updateProductQuantityInShoppingList,
        ShoppingListActions.removeProductFromShoppingList,
        ShoppingListActions.addGroupToShoppingList,
        ShoppingListActions.updateShoppingListGroupName,
        ShoppingListActions.removeGroupFromShoppingList,
        ShoppingListActions.addCommentToShoppingList,
        ShoppingListActions.updateCommentInShoppingList,
        ShoppingListActions.removeCommentFromShoppingList,
        state => ({
            ...state,
            loading: true,
            error: null,
        }),
    ),
    // #endregion

    // #region Shopping List
    on(ShoppingListActions.getAllShoppingListsSuccess, (state, { shoppingLists }) => ({
        ...state,
        shoppingLists,
        loading: false,
    })),

    // These all return a complete ShoppingListDto -- just swap the whole list in
    // state for the one we got back, don't try to merge product-by-product.
    on(
        ShoppingListActions.getShoppingListSuccess,
        ShoppingListActions.createShoppingListSuccess,
        ShoppingListActions.updateShoppingListNameSuccess,
        ShoppingListActions.addProductToShoppingListSuccess,
        ShoppingListActions.createShoppingListAndAddProductSuccess,
        (state, { shoppingList }) => ({ ...upsertShoppingList(state, shoppingList), loading: false }),
    ),

    on(ShoppingListActions.deleteShoppingListSuccess, (state, { shoppingListId }) => ({
        ...state,
        loading: false,
        shoppingLists: state.shoppingLists.filter(list => list.id !== shoppingListId),
    })),
    // #endregion

    // #region Groups
    // Only the group comes back, not the whole list -- patch it into the list we sent shoppingListId for.
    on(ShoppingListActions.addGroupToShoppingListSuccess, (state, { shoppingListId, shoppingListGroup }) => ({
        ...state,
        loading: false,
        shoppingLists: state.shoppingLists.map(list =>
            list.id === shoppingListId ? { ...list, shoppingListGroup: [...list.shoppingListGroup, shoppingListGroup] } : list,
        ),
    })),

    on(ShoppingListActions.updateShoppingListGroupNameSuccess, (state, { shoppingListGroup }) => ({
        ...state,
        loading: false,
        shoppingLists: state.shoppingLists.map(list => {
            const hasGroup = list.shoppingListGroup.some(g => g.id === shoppingListGroup.id);
            if (!hasGroup) return list;
            return {
                ...list,
                shoppingListGroup: list.shoppingListGroup.map(g => (g.id === shoppingListGroup.id ? shoppingListGroup : g)),
            };
        }),
    })),

    on(ShoppingListActions.removeGroupFromShoppingListSuccess, (state, { shoppingListGroupId }) => ({
        ...state,
        loading: false,
        shoppingLists: state.shoppingLists.map(list => {
            const hasGroup = list.shoppingListGroup.some(g => g.id === shoppingListGroupId);
            if (!hasGroup) return list;

            const removedProductIds = list.shoppingListProduct.filter(p => p.shoppingListGroupId === shoppingListGroupId).map(p => p.id);

            return {
                ...list,
                shoppingListGroup: list.shoppingListGroup.filter(g => g.id !== shoppingListGroupId),
                shoppingListProduct: list.shoppingListProduct.filter(p => p.shoppingListGroupId !== shoppingListGroupId),
                shoppingListComment: list.shoppingListComment.filter(
                    c => c.shoppingListGroupId !== shoppingListGroupId && !removedProductIds.includes(c.shoppingListProductId!),
                ),
            };
        }),
    })),
    // #endregion

    // #region Products
    // These two only get back the single product/id that changed, so we patch it in ourselves.
    on(ShoppingListActions.updateProductQuantityInShoppingListSuccess, (state, { shoppingListProduct }) => ({
        ...state,
        loading: false,
        shoppingLists: state.shoppingLists.map(list =>
            list.id === shoppingListProduct.shoppingListId
                ? {
                      ...list,
                      shoppingListProduct: list.shoppingListProduct.map(p => (p.id === shoppingListProduct.id ? shoppingListProduct : p)),
                  }
                : list,
        ),
    })),

    on(ShoppingListActions.removeProductFromShoppingListSuccess, (state, { shoppingListProductId }) => ({
        ...state,
        loading: false,
        shoppingLists: state.shoppingLists.map(list => ({
            ...list,
            shoppingListProduct: list.shoppingListProduct.filter(p => p.id !== shoppingListProductId),
            shoppingListComment: list.shoppingListComment.filter(c => c.shoppingListProductId !== shoppingListProductId),
        })),
    })),
    // #endregion

    // #region Comments
    on(ShoppingListActions.addCommentToShoppingListSuccess, (state, { shoppingListComment }) => ({
        ...state,
        loading: false,
        shoppingLists: state.shoppingLists.map(list =>
            list.id === shoppingListComment.shoppingListId
                ? { ...list, shoppingListComment: [...list.shoppingListComment, shoppingListComment] }
                : list,
        ),
    })),

    on(ShoppingListActions.updateCommentInShoppingListSuccess, (state, { shoppingListComment }) => ({
        ...state,
        loading: false,
        shoppingLists: state.shoppingLists.map(list =>
            list.id === shoppingListComment.shoppingListId
                ? {
                      ...list,
                      shoppingListComment: list.shoppingListComment.map(c => (c.id === shoppingListComment.id ? shoppingListComment : c)),
                  }
                : list,
        ),
    })),

    on(ShoppingListActions.removeCommentFromShoppingListSuccess, (state, { shoppingListCommentId }) => ({
        ...state,
        loading: false,
        shoppingLists: state.shoppingLists.map(list => ({
            ...list,
            shoppingListComment: list.shoppingListComment.filter(c => c.id !== shoppingListCommentId),
        })),
    })),
    // #endregion

    // #region Failure
    on(
        ShoppingListActions.getAllShoppingListsFailure,
        ShoppingListActions.getShoppingListFailure,
        ShoppingListActions.createShoppingListFailure,
        ShoppingListActions.updateShoppingListNameFailure,
        ShoppingListActions.deleteShoppingListFailure,
        ShoppingListActions.addProductToShoppingListFailure,
        ShoppingListActions.createShoppingListAndAddProductFailure,
        ShoppingListActions.updateProductQuantityInShoppingListFailure,
        ShoppingListActions.removeProductFromShoppingListFailure,
        ShoppingListActions.addGroupToShoppingListFailure,
        ShoppingListActions.updateShoppingListGroupNameFailure,
        ShoppingListActions.removeGroupFromShoppingListFailure,
        ShoppingListActions.removeCommentFromShoppingListFailure,
        ShoppingListActions.addCommentToShoppingListFailure,
        ShoppingListActions.updateCommentInShoppingListFailure,
        (state, { error }) => ({
            ...state,
            loading: false,
            error,
        }),
    ),
    // #endregion
);
